import { request } from '@/utils/request.js';

/**
 * 聊天功能 Mixin
 * 包含：会话恢复、欢迎语、消息发送/接收、打字机效果
 */
export default {
    data() {
        return {
            chatConversationId: '', // Dify会话ID
            chatIsVoiceMode: false, // 是否为语音输入模式
            chatIsTyping: false, // 是否正在打字效果中
            chatTypingTimer: null, // 打字机效果计时器
            chatTypewriterSpeed: 50, // 打字机速度（毫秒/字符）
            chatThinkingText: '思考中', // 思考中提示文案
            chatThinkingTimer: null, // 思考中文案切换计时器
        };
    },

    methods: {
        // 恢复当前未结束的会话
        chatRestoreConversation() {
            return this.chatCheckAndSendWelcome();
        },

        // 检查是否需要发送欢迎语（按 chatMode 只恢复对应类型的会话：chat-普通聊天 assessment-心理评估）
        async chatCheckAndSendWelcome() {
            try {
                const convRes = await request({
                    url: '/api/current-conversation',
                    method: 'GET',
                    data: { type: this.chatMode },
                });

                if (convRes.data.code === 200) {
                    const { conversationId, isReport, messages } =
                        convRes.data.data;
                    if (conversationId && messages && messages.length > 0) {
                        this.chatConversationId = conversationId;
                        this.messages = messages;
                        this.chatScrollToBottom();
                        // 评估会话恢复后自动进入评估模式（不发初始消息）
                        if (isReport) {
                            this.isAssessmentMode = true;
                            this.isAssessmentWaiting = true;
                        }
                        return;
                    }
                    if (!conversationId) {
                        this.chatShowDefaultWelcome();
                    }
                }
            } catch (err) {
                console.error('获取会话失败:', err);
                this.chatShowDefaultWelcome();
            }
        },

        // 显示默认欢迎语
        chatShowDefaultWelcome() {
            if (this.messages.length === 0) {
                this.messages = [
                    {
                        type: 'bot',
                        content:
                            '你好！我是小酒窝，有什么想聊的可以随时告诉我~',
                    },
                ];
            }
        },

        // 发送文字消息
        chatSendMessage() {
            const text = this.inputText.trim();
            if (!text) return;

            // 用户首次交互时解锁浏览器音频自动播放
            this.audioUnlock();

            this.messages.push({
                type: 'user',
                content: text,
            });

            this.inputText = '';
            this.chatScrollToBottom();

            if (this.isAssessmentMode) {
                this.assessmentSubmitAnswer(text);
            } else {
                this.chatSendToBackend(text);
            }
        },

        // 调用后端发送文字消息接口（阻塞式，返回完整回复+音频URL）
        async chatSendToBackend(text, userAudioFile = null) {
            const msgIndex = this.messages.length;
            this.messages.push({ type: 'thinking' });
            this.chatScrollToBottom();

            try {
                const res = await request({
                    url: '/api/send-message',
                    method: 'POST',
                    data: {
                        message: text,
                        conversationId: this.chatConversationId,
                        userAudioFile: userAudioFile,
                    },
                });

                const data = res.data;
                if (data.code === 200) {
                    const reply = data.data.difyReply || '';
                    const audioUrl = data.data.audioUrl;

                    // 用打字机效果展示回复文字
                    if (reply) {
                        this.chatStartTypewriter(reply, msgIndex);
                    } else {
                        this.$set(this.messages, msgIndex, {
                            type: 'bot',
                            content: '抱歉，出了点问题，请重试~',
                        });
                    }

                    // 更新会话ID
                    if (data.data.conversationId) {
                        this.chatConversationId = data.data.conversationId;
                    }

                    // 播放语音
                    if (audioUrl) {
                        this.audioPlay(audioUrl);
                    }
                } else {
                    this.$set(this.messages, msgIndex, {
                        type: 'bot',
                        content: data.message || '抱歉，出了点问题，请重试~',
                    });
                    this.$toast(data.message || '发送失败');
                }
            } catch (err) {
                console.error('请求失败:', err);
                // 401 踢下线场景：request.js 已统一弹提示并跳登录页，此处静默避免「网络错误」盖掉提示
                if (err.isAuthError) return;
                // thinking 气泡替换为错误提示气泡（$set 保证响应式）
                this.$set(this.messages, msgIndex, {
                    type: 'bot',
                    content: '网络错误，请重试~',
                });
                this.$toast('网络错误，请重试');
            }
        },

        // 打字机效果展示消息
        chatStartTypewriter(fullText, msgIndex, callback) {
            if (this.chatTypingTimer) {
                clearInterval(this.chatTypingTimer);
            }

            this.chatIsTyping = true;
            let charIndex = 0;

            // $set 替换数组元素，保证 Vue2 响应式
            this.$set(this.messages, msgIndex, { type: 'bot', content: '' });

            this.chatTypingTimer = setInterval(() => {
                if (charIndex < fullText.length) {
                    this.messages[msgIndex].content += fullText[charIndex];
                    charIndex++;
                    this.chatScrollToBottom();
                } else {
                    clearInterval(this.chatTypingTimer);
                    this.chatTypingTimer = null;
                    this.chatIsTyping = false;
                    if (callback && typeof callback === 'function') {
                        callback();
                    }
                }
            }, this.chatTypewriterSpeed);
        },

        // 停止打字机效果
        chatStopTypewriter() {
            if (this.chatTypingTimer) {
                clearInterval(this.chatTypingTimer);
                this.chatTypingTimer = null;
            }
            this.chatIsTyping = false;
        },

        // 滚动聊天区域到底部
        chatScrollToBottom() {
            this.$nextTick(() => {
                const el = this.$refs.chatArea;
                if (el) el.scrollTop = el.scrollHeight;
            });
        },
    },
};
